import { useState, useEffect } from 'react';
import { ArrowLeft, FileText, Loader2, Download, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { ResponsiveContainer } from './ResponsiveContainer';
import AdminJsonToggle from './AdminJsonToggle';
import type { Module } from '@/types/api';

interface DocumentSection {
  id: string;
  title: string;
  content: string;
  type: 'text' | 'list' | 'quote' | 'heading';
  metadata?: Record<string, unknown>;
}

interface StructuredDocumentData {
  documentType: 'case_for_change' | 'communication_strategy' | 'measurement_strategy';
  title?: string;
  description?: string;
  sections: DocumentSection[];
  metadata?: {
    generatedAt?: string;
    lastModified?: string;
    version?: string;
    projectId?: number;
    organizationId?: number;
  };
}

interface StructuredDocumentModuleProps {
  module: Module;
  onComplete?: () => void;
}

/**
 * StructuredDocumentModule displays AI-generated documents
 * Read-only view optimized for mobile with desktop enhancements
 */
export default function StructuredDocumentModule({ 
  module, 
  onComplete 
}: StructuredDocumentModuleProps) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Parse module data
  let documentData: StructuredDocumentData = { documentType: 'case_for_change', sections: [] };
  
  try {
    if (module.data) {
      const parsed = JSON.parse(module.data);
      documentData = {
        documentType: parsed.documentType || 'case_for_change',
        title: parsed.title,
        description: parsed.description,
        sections: Array.isArray(parsed.sections) ? parsed.sections : [],
        metadata: parsed.metadata
      };
    }
  } catch (error) {
    console.error('Failed to parse module data:', error);
  }

  useEffect(() => {
    // Simulate loading state for better UX
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const renderSectionContent = (section: DocumentSection) => {
    switch (section.type) {
      case 'heading':
        return (
          <h3 className="text-lg lg:text-xl font-semibold text-black mb-2">
            {section.content}
          </h3>
        );
      
      case 'list': {
        const items = section.content.split('\n').filter(item => item.trim());
        return (
          <ul className="space-y-2">
            {items.map((item, index) => (
              <li key={index} className="flex items-start">
                <span className="text-primary mr-2">•</span>
                <span className="text-sm lg:text-base text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        );
      }
      
      case 'quote':
        return (
          <blockquote className="border-l-4 border-primary/20 pl-4 py-2 italic text-gray-600">
            {section.content}
          </blockquote>
        );
      
      default:
        return (
          <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
            {section.content}
          </p>
        );
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'case_for_change':
        return 'Case for Change';
      case 'communication_strategy':
        return 'Communication Strategy';
      case 'measurement_strategy':
        return 'Measurement Strategy';
      default:
        return 'Document';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading document...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <AdminJsonToggle 
        data={{
          module,
          documentData,
          activeSection
        }}
        label="Document Module Data"
      />
      <PageHeader
        title={documentData.title || getDocumentTypeLabel(documentData.documentType)}
        variant="back-with-title"
        onBack={() => navigate('/updates')}
      />

      <ResponsiveContainer maxWidth="7xl" className="py-6">
        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Table of Contents - Desktop Only */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-6 bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Contents
              </h3>
              <nav className="space-y-2">
                {documentData.sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
                      activeSection === section.id
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Document Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg border border-gray-200 lg:shadow-sm">
              {/* Document Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                      {documentData.title || getDocumentTypeLabel(documentData.documentType)}
                    </h1>
                    {documentData.description && (
                      <p className="text-gray-600 lg:text-lg">
                        {documentData.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="hidden lg:flex"
                      onClick={() => {
                        // TODO: Implement preview functionality
                        console.log('Preview document');
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        // TODO: Implement download functionality
                        console.log('Download document');
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Metadata */}
                {documentData.metadata && (
                  <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-500">
                    {documentData.metadata.generatedAt && (
                      <span>
                        Generated: {new Date(documentData.metadata.generatedAt).toLocaleDateString()}
                      </span>
                    )}
                    {documentData.metadata.version && (
                      <span>Version: {documentData.metadata.version}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Document Sections */}
              <ScrollArea className="h-[calc(100vh-300px)] lg:h-auto">
                <div className="p-6 space-y-8">
                  {documentData.sections.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No content available</p>
                    </div>
                  ) : (
                    documentData.sections.map((section) => (
                      <section
                        key={section.id}
                        id={section.id}
                        className="scroll-mt-6"
                      >
                        <h2 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-4">
                          {section.title}
                        </h2>
                        <div className="prose prose-sm lg:prose-base max-w-none">
                          {renderSectionContent(section)}
                        </div>
                      </section>
                    ))
                  )}
                </div>
              </ScrollArea>

              {/* Action Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    {documentData.sections.length} sections
                  </p>
                  {onComplete && (
                    <Button
                      onClick={onComplete}
                      className="bg-primary hover:bg-primary/90"
                    >
                      Mark as Read
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    </div>
  );
}